import { useState, useEffect } from "react";
import { usePersonasQuery, type Persona } from "@/api/personas";
import { useToast } from "@/hooks/use-toast";
import { ManagePersonasTable } from "@/components/manage-personas/ManagePersonasTable";
import { fetchTokenPrice } from "@/api/tokens";
import { usePrivy } from "@privy-io/react-auth";

export function AllAgentsTab({ searchQuery }: { searchQuery: string }) {
  const { authenticated } = usePrivy();
  const {
    data: personas = [],
    isError,
    isPending,
  } = usePersonasQuery(authenticated);
  const { toast } = useToast();

  const filteredPersonas = personas.filter(
    (persona) =>
      persona.web3?.contractAddress &&
      (persona.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        persona.persona_id.toLowerCase().includes(searchQuery.toLowerCase())),
  );
  const [tokenMetrics, setTokenMetrics] = useState(new Map());

  useEffect(() => {
    const loadTokenInfo = async () => {
      const tokenMetricsMap = new Map();
      for (const persona of personas) {
        const tokenMetrics = await fetchMetrics(persona);
        if (tokenMetrics) {
          tokenMetricsMap.set(persona.persona_id, tokenMetrics);
        }
      }
      setTokenMetrics(tokenMetricsMap);
    };
    loadTokenInfo();

    if (isError) {
      toast({ description: "Error loading agents" });
    }
  }, [isPending, isError, toast]);

  async function fetchMetrics(personaData: Persona) {
    if (
      personaData?.web3?.pool &&
      personaData.web3.contractAddress &&
      personaData?.web3.blockchain
    ) {
      try {
        const tokenPriceData = await fetchTokenPrice(
          personaData.web3.contractAddress,
          personaData.web3.pool,
          personaData.web3.blockchain,
        );

        if (tokenPriceData?.price) {
          const price = tokenPriceData.price;
          const marketMetrics = tokenPriceData.marketMetrics;
          return {
            symbol: personaData.web3.metadata?.symbol || "",
            price: price.price?.toFixed(8) || "",
            priceChange24h: price.variation24h?.toFixed(2) || "",
            volume24h: price.volume24h || "",
            marketCap: marketMetrics?.marketCap || "",
            holders: marketMetrics?.holders || "",
            liquidity: marketMetrics?.liquidity || "",
          };
        }
      } catch (error) {
        console.error("Error fetching token metrics:", error);
      }
    }
  }

  return (
    <>
      {!isPending && filteredPersonas.length === 0 ? (
        <div className="flex justify-center py-4 mt-4">
          <p className="text-center text-muted-foreground">No agents found.</p>
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center w-full py-8 bg-darkGray border rounded-2xl *:px-2 *:md:px-4">
          {tokenMetrics && (
            <ManagePersonasTable
              personas={filteredPersonas}
              loading={isPending}
              tokenMetrics={tokenMetrics}
            />
          )}
        </div>
      )}
    </>
  );
}
